import {
  Box,
  IconButton,
  styled,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import { convertTimestamp, formatArea } from "../../../utils/formatting";
import { RUNNER_STATUS } from "../../../constants/global.constants";
import { axiosInstance } from "../../../services/api/api";
import { useEffect } from "react";
import { useResultsContext } from "../../../services/context/results-context";
import { LoaderMUI } from "../../../assets/global/loader/loader-mui";
import { luckyorangeTrackEven } from "../../../services/lucky-orange/luckyorange-events";
import { AdminRunnerItem } from "./admin-runner-item";
import { TEXT } from "../../../constants/text";
import { TooltipCustom } from "../../ui/tooltip-custom";
import { useUserContext } from "../../../services/context/user-context";

// svg
import { ReactComponent as IconBuildingsArea } from "../../../assets/svg/kpi-building-area.svg";
import { ReactComponent as IconMapPin } from "../../../assets/svg/location.svg";
import { ReactComponent as IconPropertyArea } from "../../../assets/svg/property-area.svg";
import { ReactComponent as IconTooltip } from "../../../assets/svg/help-circle.svg";
import { ReactComponent as IconDelete } from "../../../assets/svg/delete.svg";
import { ReactComponent as IconBulb } from "../../../assets/svg/bulb.svg";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.background.default,
    fontSize: 12,
    /*   boxShadow: theme.shadows[1],
     fontSize: 11 */
  },
  /* cursor: 'pointer', */
}));

export const RunnerItem = ({
  runner,
  isSelected,
  setSelectedRunner,
  index,
}) => {
  const { userData } = useUserContext();
  const { runners, setRunners } = useResultsContext();
  /*   const formattedDate = runner.createdAt?._seconds
    ? convertTimestamp(runner.createdAt._seconds, {
        isNanoseconds: false,
        toString: true,
        locale: "en-GB",
        formatOptions: { timeZone: "UTC", hour12: false },
      })
    : null; */

  const runnerStatus = runner.state;

  const isRunnerClickabel =
    runnerStatus === RUNNER_STATUS.HASRESULT ||
    runnerStatus === RUNNER_STATUS.SUCCESS;

  const getRunnerStatus = async (runnerId) => {
    try {
      const response = await axiosInstance.get(`/runner2/state/${runnerId}`);

      /* setRunnerStatus(response.data.state); */
      setRunners((prev) =>
        prev.map((r) =>
          r.id === runner.id
            ? {
                ...r,
                state: response.data.state,
                total_results: response.data.total,
                completed_results: response.data.completed,
                timings: response.data.timings ?? null,
              }
            : r,
        ),
      );
    } catch (error) {
      // Handle error
      /* setError(error.response ? error.response.data.message : error.message); */
    } finally {
      /* setLoading(false); */
    }
  };

  useEffect(() => {
    if (!runner.id) return;
    if (
      runner.state === RUNNER_STATUS.SUCCESS ||
      runner.state === RUNNER_STATUS.FAILED
    )
      return;
    const intervalId = window.setInterval(() => {
      getRunnerStatus(runner.id);
    }, 10000); // 10 seconds

    // Cleanup function to clear the interval
    return () => window.clearInterval(intervalId);
  }, [runner.state]);

  const selectRunnerHandler = () => {
    if (isRunnerClickabel) {
      luckyorangeTrackEven("Results: select Runner");
      setSelectedRunner({ ...runner, name: `Generation ${index + 1}` });
    }
  };

  const kpis = [
    {
      id: "runner-kpi-1",
      name: runner?.PropertyData?.location,
      unit: "",
      value: runner?.PropertyData?.location,
      icon: "location",
    },
    {
      id: "runner-kpi-2",
      name: TEXT.sidebarResults.site.siteAreaTitle,
      unit: formatArea(runner?.PropertyData?.area).unit,
      value: formatArea(runner?.PropertyData?.area).value,
      icon: "site_area",
    },
    /* {
      id: "runner-kpi-3",
      name: TEXT.sidebarResults.kpis.totalBuildingAreaTitle,
      unit: "",
      value: 0,
      icon: "building_area",
    }, */ {
      id: "runner-kpi-4",
      name: TEXT.sidebarResults.kpis.dateTitle,
      unit: "",
      value: convertTimestamp(runner?.createdAt?._seconds, {
        customFormat: true,
      }),
      icon: "date",
    },
  ];

  const icons = {
    location: <IconMapPin fill="#343745" width="16px" height="16px" />,
    building_area: (
      <IconBuildingsArea fill="#343745" width="16px" height="16px" />
    ),
    site_area: <IconPropertyArea fill="#343745" width="16px" height="16px" />,
    date: <IconBulb stroke="#343745" width="16px" height="16px" />,
  };

  const adminTimings = runner.timings && JSON.parse(runner.timings);
  const runnerErrorMsg = adminTimings?.errormsg
    ? adminTimings.errormsg
    : "Please go to the Master.Maker page and click on the Generate Result button to try again.";

  return (
    <Box
      onClick={selectRunnerHandler}
      sx={{
        width: "calc(50% - 4px)",
        /* maxWidth: "138px", */
        /* height: "123px", */
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "10px",
        boxShadow: 3,
        background: "#fff",
        borderRadius: "8px",
        ":hover": { boxShadow: isRunnerClickabel ? 6 : 3 },
        cursor: isRunnerClickabel ? "pointer" : "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "6px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "10px",
            height: "10px",
            background:
              runnerStatus === RUNNER_STATUS.FAILED
                ? "#BA1A1A"
                : runnerStatus === RUNNER_STATUS.SUCCESS
                  ? "#0FC23C"
                  : "#F2B54B",
            borderRadius: "50%",
            border: "1.5px solid #fff",
            boxShadow:
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
          }}
        />

        <Typography fontSize={14} fontWeight={600} whiteSpace={"nowrap"}>
          {runner.name && runner.name !== runner.id
            ? runner.name
            : "Generation"}
        </Typography>
      </Box>

      {/* Loader */}
      {(runnerStatus === RUNNER_STATUS.START ||
        runnerStatus === RUNNER_STATUS.HASRESULT) && (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ width: "100%", height: "20px", position: "relative" }}>
            <LoaderMUI withBackdrop={false} size={10} />
          </Box>

          <Typography color="#777982" fontSize="12px" fontWeight={400}>
            generating...
          </Typography>
          {runner.total_results && (
            <Typography color="#777982" fontSize="12px" fontWeight={400}>
              {runner.completed_results} of {runner.total_results}
            </Typography>
          )}
        </Box>
      )}
      {runnerStatus === RUNNER_STATUS.FAILED && (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <Typography
            color="#BA1A1A"
            fontSize="12px"
            textAlign={"center"}
            fontWeight={400}
          >
            Failed to generate results.
          </Typography>
          <LightTooltip title={runnerErrorMsg} placement="right">
            <IconTooltip width="14px" height="14px" stroke="#BA1A1A" />
          </LightTooltip>
        </Box>
      )}

      {runnerStatus === RUNNER_STATUS.SUCCESS && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            /* width: "100%", */
            width: "fit-content",
          }}
        >
          {kpis.map((kpi) =>
            kpi.value !== -1 ? (
              <TooltipCustom text={kpi.name} key={kpi.id + "tooltip"}>
                {kpi.id === "runner-kpi-1" || kpi.id === "runner-kpi-4" ? (
                  <Box
                    key={kpi.id}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      overflow: "hidden",
                    }}
                  >
                    {icons[kpi.icon]}

                    <Typography
                      fontSize={12}
                      fontWeight={400}
                      sx={{
                        textOverflow: "ellipsis",
                        width: "90px",
                        overflow: "hidden",
                        textWrap: "nowrap",
                      }}
                    >
                      {kpi.value}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    key={kpi.id}
                    sx={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      width: "fit-content",
                    }}
                  >
                    {icons[kpi.icon]}
                    <Typography ml={"4px"} fontSize={12} fontWeight={400}>
                      {kpi.value}
                    </Typography>
                    <Typography fontSize={10} fontWeight={400}>
                      {kpi.unit}
                    </Typography>
                  </Box>
                )}
              </TooltipCustom>
            ) : null,
          )}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "space-around",
          position: "relative",
        }}
      >
        <Box>
          <Typography fontSize={10} textAlign={"center"} color="#757779">
            {convertTimestamp(runner?.createdAt?._seconds, {
              customFormat: true,
            })}
          </Typography>
        </Box>
        <Box sx={{ width: "2px", height: "70%", backgroundColor: "#EBEBEC" }} />
        {false ? (
          <Box
            sx={{
              width: "30px",
              position: "relative",
            }}
          >
            <LoaderMUI size={10} />
          </Box>
        ) : (
          <IconButton padding="4px" /* onClick={favoriteHandler} */>
            <IconDelete fill="#343745" width="14px" height="14px" />
          </IconButton>
        )}
      </Box>
      {userData.isAdmin && <AdminRunnerItem runner={runner} />}
    </Box>
  );
};
